<template>
    <v-dialog v-model="dialog" max-width="800">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('editCourse') }}</span>
        </v-card-title>
  
        <v-card-text>
          <v-form ref="form">
            <v-row>
              <!-- Course Title -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedCourse.title"
                  :label="$t('title')"
                  :rules="[rules.required]"
                  clearable
                ></v-text-field>
              </v-col>
  
              <!-- Job Title -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedCourse.jobTitle"
                  :items="jobTitles"
                  :label="$t('jobTitle')"
                  :rules="[rules.required]"
                  item-text="name"
                  item-value="name"
                ></v-select>
              </v-col>
  
              <!-- Category -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedCourse.category"
                  :items="categories"
                  :label="$t('category')"
                  :rules="[rules.required]"
                  item-text="name"
                  item-value="name"
                ></v-select>
              </v-col>
  
              <!-- Description -->
              <v-col cols="12">
                <v-textarea
                  v-model="editedCourse.description"
                  :label="$t('description')"
                  :rules="[rules.required]"
                  clearable
                  rows="3"
                  auto-grow
                ></v-textarea>
              </v-col>
  
              <!-- Thumbnail Upload -->
              <v-col cols="12">
                <v-file-input
                  v-model="thumbnailFile"
                  :label="$t('thumbnail')"
                  accept="image/*"
                  prepend-icon="mdi-camera"
                  @change="handleThumbnailChange"
                  show-size
                  :rules="[rules.imageSize]"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip small label color="primary">
                      {{ text }}
                    </v-chip>
                  </template>
                </v-file-input>
              </v-col>
  
              <!-- Current Thumbnail Preview -->
              <v-col v-if="currentThumbnailUrl || thumbnailPreview" cols="12" class="text-center">
                <v-img
                  :src="thumbnailPreview || currentThumbnailUrl"
                  max-height="200"
                  contain
                  class="grey lighten-2"
                ></v-img>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
  
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="close">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn 
            color="primary" 
            text 
            @click="updateCourse"
            :loading="saving"
          >
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  import config from '../../../public/config';
  
  export default {
    name: 'EditCourseForm',
    props: {
      value: {
        type: Boolean,
        default: false
      },
      course: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        dialog: false,
        saving: false,
        thumbnailFile: null,
        thumbnailPreview: null,
        currentThumbnailUrl: null,
        config,
        rules: {
          required: (value) => !!value || this.$t('required'),
          imageSize: (file) => {
            if (!file) return true;
            const size = file.size / 1024 / 1024; // Convert to MB
            return size <= 2 || this.$t('imageSizeLimit');
          },
        },
        editedCourse: {
          courseId: null,
          title: '',
          description: '',
          category: null,
          jobTitle: null,
          thumbnail: null,
        },
        categories: [],
        jobTitles: [
          { id: 1, name: 'Software Engineer' },
          { id: 2, name: 'Data Scientist' },
          { id: 3, name: 'Product Manager' },
          { id: 4, name: 'UX Designer' },
          { id: 5, name: 'DevOps Engineer' },
          { id: 6, name: 'QA Engineer' },
          { id: 7, name: 'Technical Writer' },
          { id: 8, name: 'Scrum Master' },
          { id: 9, name: 'Business Analyst' },
          { id: 10, name: 'Systems Analyst' },
        ],
      };
    },
    watch: {
      value(newVal) {
        this.dialog = newVal;
      },
      dialog(newVal) {
        this.$emit('input', newVal);
        if (!newVal) {
          this.resetForm();
        }
      },
      course: {
        handler(newCourse) {
          if (newCourse) {
            this.initializeForm(newCourse);
          }
        },
        immediate: true
      }
    },
    created() {
      this.fetchCategories();
    },
    methods: {
      initializeForm(course) {
        this.editedCourse = { ...course };
        if (course.thumbnail) {
          this.currentThumbnailUrl = `${this.config.hostname}/${course.thumbnail}`;
        }
      },
      fetchCategories() {
        this.$store
          .dispatch('course/fetchCategories')
          .then((response) => {
            this.categories = response.data;
          })
          .catch((error) => {
            console.error('Error fetching categories:', error);
            this.$emit('error', this.$t('errorFetchingCategories'));
          });
      },
      handleThumbnailChange(file) {
        if (!file) {
          this.thumbnailPreview = null;
          return;
        }
  
        this.thumbnailPreview = URL.createObjectURL(file);
        
        return () => {
          URL.revokeObjectURL(this.thumbnailPreview);
        };
      },
      async updateCourse() {
        if (this.$refs.form.validate()) {
          this.saving = true;
          
          try {
            // Handle thumbnail upload if a new file was selected
            if (this.thumbnailFile) {
              const formData = new FormData();
              formData.append('thumbnail', this.thumbnailFile);
              
              const uploadResponse = await this.$store.dispatch('course/uploadCourseThumbnail', formData);
              this.editedCourse.thumbnail = uploadResponse.data.dbPath;
            }

            console.log('editedCourse:', this.editedCourse);
  
            // Update course
            const response = await this.$store.dispatch('course/updateCourse', this.editedCourse);
            console.log('Course updated:', response.data);
            this.$emit('courseUpdated');
            this.$emit('success', this.$t('courseUpdatedSuccess'));
            this.dialog = false;
            
          } catch (error) {
            console.error('Error updating course:', error);
            this.$emit('error', this.$t('errorUpdatingCourse'));
          } finally {
            this.saving = false;
          }
        }
      },
      resetForm() {
        if (this.$refs.form) {
          this.$refs.form.reset();
        }
        this.thumbnailPreview = null;
        this.thumbnailFile = null;
        this.currentThumbnailUrl = null;
      },
      close() {
        this.dialog = false;
      },
    },
    beforeDestroy() {
      if (this.thumbnailPreview) {
        URL.revokeObjectURL(this.thumbnailPreview);
      }
    },
  };
  </script>